import React from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BellIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import { Line, Doughnut } from 'react-chartjs-2';
import { format } from 'date-fns';
import { createDeferredChart } from './charts/DeferredChart';

import { NutritionalTrendData, NutritionalAnalytics } from '../api/nutrition';

const DeferredLine = createDeferredChart(Line);
const DeferredDoughnut = createDeferredChart(Doughnut);

interface NutritionalDashboardProps {
  data: {
    period: { days: number; startDate: string; endDate: string };
    trends: NutritionalTrendData[];
    analytics: NutritionalAnalytics;
    dashboard: {
      totalMixes: number;
      recentMixes: number;
      pendingApprovals: number;
      qualityIssues: number;
      averageQualityScore: number;
      nutritionalStability: {
        protein: string;
        fat: string;
        fiber: string;
        carbohydrate: string;
      };
    };
    alerts: Array<{
      type: 'deficiency' | 'excess' | 'imbalance';
      nutrient: string;
      severity: 'low' | 'medium' | 'high';
      message: string;
    }>;
    recommendations: string[];
  };
  trends: { data: NutritionalTrendData[]; analytics: NutritionalAnalytics } | null;
  onRefresh: () => void;
}

export default function NutritionalDashboard({ data, trends, onRefresh }: NutritionalDashboardProps) {
  const {
    dashboard,
    analytics,
    alerts,
    recommendations,
    period
  } = data;

  // Chart configurations
  const nutritionalTrendsChartData = {
    labels: trends?.data.map(d => format(new Date(d.date), 'MMM dd')) || [],
    datasets: [
      {
        label: 'Protein %',
        data: trends?.data.map(d => d.proteinPercentage) || [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Fat %',
        data: trends?.data.map(d => d.fatPercentage) || [],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Fiber %',
        data: trends?.data.map(d => d.fiberPercentage) || [],
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Carbohydrate %',
        data: trends?.data.map(d => d.carbohydratePercentage || 0) || [],
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const qualityScoreChartData = {
    labels: trends?.data.map(d => format(new Date(d.date), 'MMM dd')) || [],
    datasets: [
      {
        label: 'Quality Score',
        data: trends?.data.map(d => d.qualityScore) || [],
        borderColor: 'rgb(139, 69, 19)',
        backgroundColor: 'rgba(139, 69, 19, 0.1)',
        fill: true,
        tension: 0.1,
      },
    ],
  };

  const stabilityChartData = {
    labels: ['Protein', 'Fat', 'Fiber', 'Carbohydrate'],
    datasets: [
      {
        data: [
          dashboard.nutritionalStability.protein === 'stable' ? 1 : 0,
          dashboard.nutritionalStability.fat === 'stable' ? 1 : 0,
          dashboard.nutritionalStability.fiber === 'stable' ? 1 : 0,
          dashboard.nutritionalStability.carbohydrate === 'stable' ? 1 : 0,
        ],
        backgroundColor: [
          dashboard.nutritionalStability.protein === 'stable' ? '#10B981' : '#EF4444',
          dashboard.nutritionalStability.fat === 'stable' ? '#10B981' : '#EF4444',
          dashboard.nutritionalStability.fiber === 'stable' ? '#10B981' : '#EF4444',
          dashboard.nutritionalStability.carbohydrate === 'stable' ? '#10B981' : '#EF4444',
        ],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
      case 'declining':
        return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
      default:
        return <div className="h-4 w-4 bg-gray-400 rounded-full"></div>;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'improving':
        return 'text-green-700';
      case 'declining':
        return 'text-red-700';
      default:
        return 'text-slate-700';
    }
  };

  const getStabilityColor = (stability: string) => {
    return stability === 'stable' ? 'text-green-600' : 'text-yellow-600';
  };

  const getAlertIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'medium':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <BellIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const getAlertBgColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-50 border-red-200';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-cyan-50 border-blue-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BeakerIcon className="h-8 w-8 text-cyan-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-slate-500 truncate">Total Mixes</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-slate-900">{dashboard.totalMixes}</div>
                  <div className="ml-2 text-sm text-slate-600">
                    Last {period.days} days
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-slate-500 truncate">Avg Quality Score</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-slate-900">
                    {dashboard.averageQualityScore.toFixed(1)}
                  </div>
                  <div className="ml-2 text-sm text-slate-600">/100</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-slate-500 truncate">Pending Approvals</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-slate-900">{dashboard.pendingApprovals}</div>
                  {dashboard.pendingApprovals > 0 && (
                    <div className="ml-2 text-sm text-yellow-600">Need attention</div>
                  )}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-slate-500 truncate">Quality Issues</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-slate-900">{dashboard.qualityIssues}</div>
                  {dashboard.qualityIssues > 0 && (
                    <div className="ml-2 text-sm text-red-600">Need review</div>
                  )}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Nutritional Trends Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-900">Nutritional Trends</h3>
            <button
              onClick={onRefresh}
              className="text-sm text-cyan-600 hover:text-cyan-500"
            >
              Refresh
            </button>
          </div>
          <div className="h-64">
            <DeferredLine
              chartKey="dashboard-nutritional-trends"
              fallbackHeight={256}
              data={nutritionalTrendsChartData}
              options={{ ...chartOptions, maintainAspectRatio: false }}
            />
          </div>
        </div>

        {/* Quality Score Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Quality Score Trend</h3>
          <div className="h-64">
            <DeferredLine
              chartKey="dashboard-quality-score"
              fallbackHeight={256}
              data={qualityScoreChartData}
              options={{
                ...chartOptions,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    min: 0,
                    max: 100,
                    title: {
                      display: true,
                      text: 'Quality Score'
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Analytics Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Nutritional Averages */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Nutritional Averages</h3>
          <dl className="space-y-3">
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-500">Protein:</dt>
              <dd className="text-sm font-medium text-slate-900">
                {analytics.averages.proteinPercentage.toFixed(1)}%
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-500">Fat:</dt>
              <dd className="text-sm font-medium text-slate-900">
                {analytics.averages.fatPercentage.toFixed(1)}%
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-500">Fiber:</dt>
              <dd className="text-sm font-medium text-slate-900">
                {analytics.averages.fiberPercentage.toFixed(1)}%
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-500">Carbohydrate:</dt>
              <dd className="text-sm font-medium text-slate-900">
                {(analytics.averages.carbohydratePercentage || 0).toFixed(1)}%
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-500">Daily Quantity:</dt>
              <dd className="text-sm font-medium text-slate-900">
                {analytics.averages.dailyQuantity.toFixed(1)} kg
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-500">Daily Calories:</dt>
              <dd className="text-sm font-medium text-slate-900">
                {analytics.averages.dailyCalories.toFixed(0)} kcal
              </dd>
            </div>
          </dl>
        </div>

        {/* Trend Analysis */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Trend Analysis</h3>
          <dl className="space-y-3">
            {Object.entries(analytics.trends).map(([key, value]) => (
              <div key={key} className="flex justify-between items-center">
                <dt className="text-sm text-slate-500 capitalize">{key}:</dt>
                <dd className="flex items-center">
                  {getTrendIcon(value)}
                  <span className={`ml-2 text-sm font-medium ${getTrendColor(value)}`}>
                    {value}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Nutritional Stability */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Nutritional Stability</h3>
          <dl className="space-y-3">
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-500">Protein:</dt>
              <dd className={`text-sm font-medium ${getStabilityColor(dashboard.nutritionalStability.protein)}`}>
                {dashboard.nutritionalStability.protein}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-500">Fat:</dt>
              <dd className={`text-sm font-medium ${getStabilityColor(dashboard.nutritionalStability.fat)}`}>
                {dashboard.nutritionalStability.fat}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-500">Fiber:</dt>
              <dd className={`text-sm font-medium ${getStabilityColor(dashboard.nutritionalStability.fiber)}`}>
                {dashboard.nutritionalStability.fiber}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-slate-500">Carbohydrate:</dt>
              <dd className={`text-sm font-medium ${getStabilityColor(dashboard.nutritionalStability.carbohydrate || 'stable')}`}>
                {dashboard.nutritionalStability.carbohydrate || 'stable'}
              </dd>
            </div>
          </dl>

          {/* Stability Visualization */}
          <div className="mt-4 h-32">
            <DeferredDoughnut
              chartKey="dashboard-stability"
              fallbackHeight={128}
              data={stabilityChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Variability Analysis */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Variability Analysis (Standard Deviation)</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-cyan-600">
              ±{analytics.variability.proteinStdDev.toFixed(2)}%
            </div>
            <div className="text-sm text-slate-500">Protein</div>
            <div className="text-xs text-slate-400 mt-1">
              {analytics.variability.proteinStdDev < 2 ? 'Very Stable' : 
               analytics.variability.proteinStdDev < 4 ? 'Stable' : 'Variable'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              ±{analytics.variability.fatStdDev.toFixed(2)}%
            </div>
            <div className="text-sm text-slate-500">Fat</div>
            <div className="text-xs text-slate-400 mt-1">
              {analytics.variability.fatStdDev < 1 ? 'Very Stable' : 
               analytics.variability.fatStdDev < 2 ? 'Stable' : 'Variable'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              ±{analytics.variability.fiberStdDev.toFixed(2)}%
            </div>
            <div className="text-sm text-slate-500">Fiber</div>
            <div className="text-xs text-slate-400 mt-1">
              {analytics.variability.fiberStdDev < 2 ? 'Very Stable' : 
               analytics.variability.fiberStdDev < 4 ? 'Stable' : 'Variable'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              ±{(analytics.variability.carbohydrateStdDev || 0).toFixed(2)}%
            </div>
            <div className="text-sm text-slate-500">Carbohydrate</div>
            <div className="text-xs text-slate-400 mt-1">
              {(analytics.variability.carbohydrateStdDev || 0) < 3 ? 'Very Stable' : 
               (analytics.variability.carbohydrateStdDev || 0) < 6 ? 'Stable' : 'Variable'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              ±{analytics.variability.qualityStdDev.toFixed(2)}
            </div>
            <div className="text-sm text-slate-500">Quality Score</div>
            <div className="text-xs text-slate-400 mt-1">
              {analytics.variability.qualityStdDev < 5 ? 'Very Stable' : 
               analytics.variability.qualityStdDev < 10 ? 'Stable' : 'Variable'}
            </div>
          </div>
        </div>
      </div>

      {/* Alerts and Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">
            Active Alerts ({alerts.length})
          </h3>
          {alerts.length > 0 ? (
            <div className="space-y-3">
              {alerts.map((alert, index) => (
                <div key={index} className={`border rounded-md p-3 ${getAlertBgColor(alert.severity)}`}>
                  <div className="flex">
                    {getAlertIcon(alert.severity)}
                    <div className="ml-3 flex-1">
                      <h4 className="text-sm font-medium text-slate-900">
                        {alert.severity.toUpperCase()}: {alert.nutrient} {alert.type}
                      </h4>
                      <p className="mt-1 text-sm text-slate-700">{alert.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No Active Alerts</h3>
              <p className="mt-1 text-sm text-slate-500">All nutritional parameters are within acceptable ranges.</p>
            </div>
          )}
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">
            Recommendations ({recommendations.length})
          </h3>
          {recommendations.length > 0 ? (
            <ul className="space-y-3">
              {recommendations.map((rec, index) => (
                <li key={index} className="flex items-start">
                  <div className="flex-shrink-0 h-1.5 w-1.5 rounded-full bg-blue-400 mt-2 mr-3"></div>
                  <span className="text-sm text-slate-700">{rec}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-8">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
              <h3 className="mt-2 text-sm font-medium text-slate-900">No Recommendations</h3>
              <p className="mt-1 text-sm text-slate-500">Your feeding program is performing optimally.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 